/**
 * datetime — locale + timezone aware date/time helpers for the render layer.
 *
 * Hemmaklubben hardcoded `sv-SE` and `Europe/Stockholm`. The render layer
 * is multi-tenant: locale and timezone come from
 * `runtimeConfig.market.locale` and `runtimeConfig.market.timezone`, which
 * the caller resolves via `useTenantConfig()` and passes in here.
 *
 * Keeping these helpers pure (no React, no i18next reach-around) makes
 * them safe to use in tests and non-component code paths.
 */

// Tenant-neutral fallbacks — only fire when a caller forgets to pass a
// locale + timezone (e.g. while tenant config is still resolving, where
// the chrome shouldn't render anyway). `UTC` is the universally safe
// default; the previous `Europe/Stockholm` value was Hemmaklubben-era
// hardcoding.
const FALLBACK_LOCALE = 'en-GB';
const FALLBACK_TIMEZONE = 'UTC';

function safeDate(iso: string): Date {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? new Date(0) : d;
}

/**
 * Format an ISO datetime as a short kickoff label like "Sat 11 May" in
 * en-GB, "lör 11 maj" in sv-SE. Used on lobby cards under the headline.
 *
 * @example
 *   formatKickoffShort('2026-06-15T17:00:00Z', 'en-GB', 'Europe/London')
 *   // → "Mon 15 Jun"
 * @example
 *   formatKickoffShort('2026-06-15T17:00:00Z', 'sv-SE', 'Europe/Stockholm')
 *   // → "mån 15 juni" (locale-dependent)
 * @example
 *   formatKickoffShort('2026-06-15T17:00:00Z', 'da-DK', 'Europe/Copenhagen')
 *   // → "man. 15. jun."
 */
export function formatKickoffShort(
  iso: string,
  locale: string = FALLBACK_LOCALE,
  timeZone: string = FALLBACK_TIMEZONE,
): string {
  return safeDate(iso).toLocaleDateString(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone,
  });
}

/**
 * Format an ISO datetime as the clock time only — "19.00" in da-DK,
 * "19:00" in en-GB. The one number a coupon row and an event header both
 * lead with, and it was a private helper inside the fixture list until the
 * event header needed the same thing.
 */
export function formatKickoffTime(
  iso: string,
  locale: string = FALLBACK_LOCALE,
  timeZone: string = FALLBACK_TIMEZONE,
): string {
  return safeDate(iso).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  });
}

/**
 * Format an ISO datetime as a long, human-readable kickoff label.
 *
 *   "Saturday 15 June 2026 at 19:00" — exact wording is locale-driven.
 */
export function formatKickoffLong(
  iso: string,
  locale: string = FALLBACK_LOCALE,
  timeZone: string = FALLBACK_TIMEZONE,
): string {
  const d = safeDate(iso);
  const date = d.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone,
  });
  const time = d.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  });
  return `${date} ${time}`;
}

/**
 * Format `publishedAt` relative to "now" for recent timestamps and as
 * an absolute short date for older ones (>= 7 days). Uses
 * `Intl.RelativeTimeFormat` for the relative part.
 */
export function formatPublishedAt(
  iso: string,
  locale: string = FALLBACK_LOCALE,
  timeZone: string = FALLBACK_TIMEZONE,
  now: number = Date.now(),
): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const diffMs = t - now;
  const absMs = Math.abs(diffMs);
  const oneDay = 24 * 60 * 60 * 1000;

  if (absMs >= 7 * oneDay) {
    return publishDateShort(iso, locale, timeZone);
  }

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const minute = 60 * 1000;
  const hour = 60 * minute;
  if (absMs < hour) {
    const minutes = Math.round(diffMs / minute);
    return rtf.format(minutes, 'minute');
  }
  if (absMs < oneDay) {
    const hours = Math.round(diffMs / hour);
    return rtf.format(hours, 'hour');
  }
  const days = Math.round(diffMs / oneDay);
  return rtf.format(days, 'day');
}

/**
 * Format an ISO datetime as a numeric short date like "11 May" — no
 * weekday, used in DefaultCard meta line. Output formatting follows the
 * supplied locale's conventions.
 */
export function publishDateShort(
  iso: string,
  locale: string = FALLBACK_LOCALE,
  timeZone: string = FALLBACK_TIMEZONE,
): string {
  return safeDate(iso).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    timeZone,
  });
}

/**
 * Format a date as numeric `dd/MM` (or whatever the locale equivalent
 * is). Used in IHS Last5Games rows where space is tight. Source date may
 * be either a YYYY-MM-DD string (engine common case) or any ISO string.
 */
export function dateNumericShort(
  iso: string,
  locale: string = FALLBACK_LOCALE,
  timeZone: string = FALLBACK_TIMEZONE,
): string {
  return safeDate(iso).toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    timeZone,
  });
}

/**
 * Full numeric date like `15/06/2026` (locale-dependent).
 */
export function formatDateNumeric(
  iso: string,
  locale: string = FALLBACK_LOCALE,
  timeZone: string = FALLBACK_TIMEZONE,
): string {
  return safeDate(iso).toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone,
  });
}

// ---------------------------------------------------------------------------
// Backwards-compatibility shims — the previous P1–P9 callers used the
// shorter names. Keep them as thin aliases so we don't break callsites.
// ---------------------------------------------------------------------------

/** @deprecated use {@link formatKickoffShort}. */
export const kickoffDateShort = formatKickoffShort;
